import { useState } from 'react'
import type { TypicalSpend } from '@fuelfinder/shared'

interface SpendSelectorProps {
  value: TypicalSpend
  onChange: (spend: TypicalSpend) => void
}

const PRESET_OPTIONS = [20, 40, 60, 80, 100]

/**
 * Button group for selecting typical fuel spend amount with custom input option
 */
export function SpendSelector({ value, onChange }: SpendSelectorProps) {
  const [customMode, setCustomMode] = useState(false)
  const [customValue, setCustomValue] = useState('')

  const isPreset = value !== null && PRESET_OPTIONS.includes(value)
  const showCustom = customMode || (!isPreset && value !== null)

  const handlePresetClick = (amount: number) => {
    setCustomMode(false)
    setCustomValue('')
    onChange(amount)
  }

  const handleCustomClick = () => {
    setCustomMode(true)
    if (value !== null && !PRESET_OPTIONS.includes(value)) {
      setCustomValue(value.toString())
    }
  }

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setCustomValue(val)
    const num = parseInt(val, 10)
    if (!isNaN(num) && num > 0 && num <= 500) {
      onChange(num)
    }
  }

  const handleCustomBlur = () => {
    const num = parseInt(customValue, 10)
    if (isNaN(num) || num <= 0) {
      // Revert to default if invalid
      setCustomMode(false)
      setCustomValue('')
      if (value === null || (!PRESET_OPTIONS.includes(value) && isNaN(parseInt(customValue, 10)))) {
        onChange(40)
      }
    }
  }

  return (
    <div className="space-y-3">
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2">
        {PRESET_OPTIONS.map((amount) => (
          <button
            key={amount}
            onClick={() => handlePresetClick(amount)}
            className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
              value === amount && !showCustom
                ? 'bg-green-500 text-white shadow-sm'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
            aria-pressed={value === amount && !showCustom}
          >
            £{amount}
          </button>
        ))}
        <button
          onClick={handleCustomClick}
          className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
            showCustom
              ? 'bg-green-500 text-white shadow-sm'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
          aria-pressed={showCustom}
        >
          Custom
        </button>
      </div>

      {/* Custom input */}
      {showCustom && (
        <div className="flex items-center gap-2">
          <span className="text-slate-500 dark:text-slate-400 text-lg">£</span>
          <input
            type="number"
            min="1"
            max="500"
            value={customValue || (value !== null && !PRESET_OPTIONS.includes(value) ? value : '')}
            onChange={handleCustomChange}
            onBlur={handleCustomBlur}
            placeholder="Enter amount"
            className="flex-1 py-2 px-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[44px]"
            autoFocus
          />
        </div>
      )}
    </div>
  )
}
